/**
 * Multi-Region Configuration System
 *
 * This file provides centralized configuration for region-specific phone functionality.
 * Adding support for a new country/region requires only updating this configuration.
 */

export type RegionCode = 'AU' | 'US';

export interface RegionConfig {
  code: RegionCode;
  name: string;
  countryCode: string; // E.164 country code (e.g., '+61', '+1')
  timezonePattern: RegExp; // Pattern to match organization timezone
  phoneValidation: {
    regex: RegExp;
    placeholder: string;
    errorMessage: string;
    exampleFormats: string[];
  };
  normalizePhone: (phone: string) => string;
}

export const REGIONS: Record<RegionCode, RegionConfig> = {
  AU: {
    code: 'AU',
    name: 'Australia',
    countryCode: '+61',
    timezonePattern: /^Australia\//,
    phoneValidation: {
      regex: /^(\+?61)?0?4\d{8}$/,
      placeholder: '4XX XXX XXX',
      errorMessage: 'Please enter a valid Australian mobile number',
      exampleFormats: ['04XX XXX XXX', '+61 4XX XXX XXX'],
    },
    normalizePhone: (phone: string) => {
      const cleaned = phone.replace(/[\s\-\(\)\.]/g, '');
      if (cleaned.startsWith('+61')) return cleaned;
      if (cleaned.startsWith('61')) return `+${cleaned}`;
      if (cleaned.startsWith('04')) return `+61${cleaned.slice(1)}`;
      if (cleaned.startsWith('4')) return `+61${cleaned}`;
      return `+61${cleaned}`;
    },
  },
  US: {
    code: 'US',
    name: 'United States',
    countryCode: '+1',
    timezonePattern: /^(America|US)\//,
    phoneValidation: {
      // US phone validation:
      // - First digit: 2-9 (area code cannot start with 0 or 1)
      // - Middle digit of area code: 2-9
      // - Total: 10 digits (excluding country code)
      regex: /^(\+?1)?[2-9]\d{2}[2-9]\d{6}$/,
      placeholder: '(555) 123-4567',
      errorMessage: 'Please enter a valid US phone number',
      exampleFormats: ['(555) 123-4567', '+1 555-123-4567', '555-123-4567'],
    },
    normalizePhone: (phone: string) => {
      const cleaned = phone.replace(/[\s\-\(\)\.]/g, '');
      if (cleaned.startsWith('+1')) return cleaned;
      if (cleaned.startsWith('1') && cleaned.length === 11) return `+${cleaned}`;
      return `+1${cleaned}`;
    },
  },
};

/**
 * Detects the region code based on organization timezone
 * @param timezone - Organization timezone (e.g., 'Australia/Sydney', 'America/New_York', 'US/Eastern')
 * @returns Region code ('AU', 'US') or null if not supported
 */
export const detectRegionFromTimezone = (timezone: string): RegionCode | null => {
  for (const [code, config] of Object.entries(REGIONS)) {
    if (config.timezonePattern.test(timezone)) {
      return code as RegionCode;
    }
  }
  return null;
};

/**
 * Checks if a timezone is supported for phone functionality
 * @param timezone - Organization timezone
 * @returns true if timezone is supported, false otherwise
 */
export const isSupportedTimezone = (timezone: string): boolean => {
  return detectRegionFromTimezone(timezone) !== null;
};

/**
 * Gets the list of supported region names
 * @returns Array of region names (e.g., ['Australia', 'United States'])
 */
export const getSupportedRegionNames = (): string[] => {
  return Object.values(REGIONS).map(config => config.name);
};
